import React, { useEffect, useState } from "react";
import KlubbVelger, { getKlubbId } from "./KlubbVelger";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const STATUS_LABEL = { MOTTATT: "Mottatt", UNDER_BEHANDLING: "Under behandling", LEVERT: "Levert" };
const STATUS_ORDER = ["MOTTATT", "UNDER_BEHANDLING", "LEVERT"];
const APPROVAL_LABEL = { JA: "Ja", KONTAKTPERSON: "Avklart med kontaktperson", KOMMENTAR: "Kommentar" };

function StatusBadge({ status }) {
  const cls = status === "LEVERT" ? "bg-emerald-100 text-emerald-700" : status === "UNDER_BEHANDLING" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700";
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{STATUS_LABEL[status]}</span>;
}

export default function Bestillinger() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  async function load() {
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/orders?clubId=${getKlubbId()}`);
      if (!res.ok) throw new Error("Kunne ikke hente bestillinger");
      setOrders(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function advanceStatus(order) {
    const currentIndex = STATUS_ORDER.indexOf(order.status);
    const next = STATUS_ORDER[currentIndex + 1];
    if (!next) return;
    setUpdatingId(order._id);
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/orders/${order._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error("Kunne ikke oppdatere status");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <span className="inline-block px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold mb-3">
        Klubb
      </span>
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Bestillinger</h1>
      <p className="text-gray-500 mb-6">Bestillinger sendt fra lagenes innkjøpsansvarlige i klubbsport1.</p>

      <KlubbVelger onChange={load} />

      {loading && <p className="text-gray-400 text-sm">Laster …</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && orders.length === 0 && (
        <p className="text-gray-400 text-sm">Ingen bestillinger ennå.</p>
      )}

      <div className="space-y-3">
        {orders.map((o) => (
          <div key={o._id} className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="font-semibold text-gray-900">{o.teamId?.name || "Ukjent lag"}</p>
                <p className="text-xs text-gray-500">
                  {o.createdByPersonId?.name} · {new Date(o.createdAt).toLocaleDateString("no-NO")} · Godkjent av klubb: {APPROVAL_LABEL[o.clubApproval?.status]}
                  {o.clubApproval?.note ? ` (${o.clubApproval.note})` : ""}
                </p>
              </div>
              <StatusBadge status={o.status} />
            </div>
            <ul className="text-sm text-gray-700 mb-3 list-disc pl-5">
              {o.items.map((item, i) => (
                <li key={i}>
                  {item.quantity}× {item.productName}
                  {item.size ? ` (${item.size}${item.color ? `, ${item.color}` : ""})` : ""}
                  {item.printNotes ? ` — ${item.printNotes}` : ""}
                </li>
              ))}
            </ul>
            {o.status !== "LEVERT" && (
              <button
                onClick={() => advanceStatus(o)}
                disabled={updatingId === o._id}
                className="text-indigo-600 hover:text-indigo-500 text-sm font-medium disabled:opacity-50"
              >
                {updatingId === o._id ? "Oppdaterer …" : `Merk som ${STATUS_LABEL[STATUS_ORDER[STATUS_ORDER.indexOf(o.status) + 1]]}`}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
